// Mock data for employee referral network
// Represents a realistic referral hierarchy in a mortgage lending company

export type Employee = {
  id: string
  name: string
  title: string
  department: string
  fundingAmount: number // Total loan amount funded by this employee (in thousands)
}

export type ReferralNetwork = {
  employees: Record<string, Employee>
  referrals: Record<string, string[]> // referrerId -> array of referred employee ids
}

export const mockReferralData: ReferralNetwork = {
  employees: {
    'emp-001': {
      id: 'emp-001',
      name: 'Sarah Mitchell',
      title: 'Senior Loan Officer',
      department: 'Originations',
      fundingAmount: 2450, // $2.45M in loans funded
    },
    'emp-002': {
      id: 'emp-002',
      name: 'James Rodriguez',
      title: 'Branch Manager',
      department: 'Operations',
      fundingAmount: 3200, // $3.2M
    },
    'emp-003': {
      id: 'emp-003',
      name: 'Emily Chen',
      title: 'Mortgage Consultant',
      department: 'Sales',
      fundingAmount: 1850, // $1.85M
    },
    'emp-004': {
      id: 'emp-004',
      name: 'Michael Thompson',
      title: 'Loan Processor',
      department: 'Processing',
      fundingAmount: 980, // $980K
    },
    'emp-005': {
      id: 'emp-005',
      name: 'Jessica Williams',
      title: 'Underwriter',
      department: 'Underwriting',
      fundingAmount: 2100, // $2.1M
    },
    'emp-006': {
      id: 'emp-006',
      name: 'David Park',
      title: 'Loan Officer',
      department: 'Originations',
      fundingAmount: 1650, // $1.65M
    },
    'emp-007': {
      id: 'emp-007',
      name: 'Amanda Foster',
      title: 'Sales Associate',
      department: 'Sales',
      fundingAmount: 1200, // $1.2M
    },
    'emp-008': {
      id: 'emp-008',
      name: 'Robert Martinez',
      title: 'Processing Specialist',
      department: 'Processing',
      fundingAmount: 750, // $750K
    },
    'emp-009': {
      id: 'emp-009',
      name: 'Lisa Anderson',
      title: 'Junior Loan Officer',
      department: 'Originations',
      fundingAmount: 890, // $890K
    },
    'emp-010': {
      id: 'emp-010',
      name: 'Kevin Brown',
      title: 'Mortgage Analyst',
      department: 'Analytics',
      fundingAmount: 650, // $650K
    },
    'emp-011': {
      id: 'emp-011',
      name: 'Rachel Green',
      title: 'Client Relations',
      department: 'Sales',
      fundingAmount: 1100, // $1.1M
    },
    'emp-012': {
      id: 'emp-012',
      name: 'Thomas Wilson',
      title: 'Loan Coordinator',
      department: 'Operations',
      fundingAmount: 820, // $820K
    },
  },
  // Referral relationships: referrer -> [referred employees]
  // This creates a realistic network with varying depths
  referrals: {
    // Sarah Mitchell (emp-001): High network impact - 2 direct, many indirect
    'emp-001': ['emp-003', 'emp-004'],

    // James Rodriguez (emp-002): High direct referrals - 3 direct, some indirect
    'emp-002': ['emp-005', 'emp-006', 'emp-007'],

    // Emily Chen (emp-003): Referred by Sarah, also refers others - creates depth
    'emp-003': ['emp-008', 'emp-009'],

    // Michael Thompson (emp-004): Referred by Sarah, refers one person
    'emp-004': ['emp-010'],

    // Jessica Williams (emp-005): Referred by James, refers two people
    'emp-005': ['emp-011', 'emp-012'],

    // David Park (emp-006): Referred by James, no further referrals
    'emp-006': [],

    // Amanda Foster (emp-007): Referred by James, no further referrals
    'emp-007': [],

    // Robert Martinez (emp-008): Referred by Emily, no further referrals
    'emp-008': [],

    // Lisa Anderson (emp-009): Referred by Emily, no further referrals
    'emp-009': [],

    // Kevin Brown (emp-010): Referred by Michael, no further referrals
    'emp-010': [],

    // Rachel Green (emp-011): Referred by Jessica, no further referrals
    'emp-011': [],

    // Thomas Wilson (emp-012): Referred by Jessica, no further referrals
    'emp-012': [],
  },
}

// Network structure visualization:
//
// Sarah Mitchell (emp-001) - $2.45M personal
// ├── Emily Chen (emp-003) - $1.85M
// │   ├── Robert Martinez (emp-008) - $750K
// │   └── Lisa Anderson (emp-009) - $890K
// └── Michael Thompson (emp-004) - $980K
//     └── Kevin Brown (emp-010) - $650K
//
// James Rodriguez (emp-002) - $3.2M personal
// ├── Jessica Williams (emp-005) - $2.1M
// │   ├── Rachel Green (emp-011) - $1.1M
// │   └── Thomas Wilson (emp-012) - $820K
// ├── David Park (emp-006) - $1.65M
// └── Amanda Foster (emp-007) - $1.2M
//
// Expected fundings:
// Sarah: Direct $2.83M (Emily $1.85M + Michael $980K), Indirect $2.29M (Robert $750K + Lisa $890K + Kevin $650K)
// James: Direct $4.95M (Jessica $2.1M + David $1.65M + Amanda $1.2M), Indirect $1.92M (Rachel $1.1M + Thomas $820K)
// Emily: Direct $1.64M (Robert $750K + Lisa $890K), Indirect $0
// Michael: Direct $650K (Kevin), Indirect $0
// Jessica: Direct $1.92M (Rachel $1.1M + Thomas $820K), Indirect $0
